import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Scale, ChevronDown, ChevronRight, Moon, CalendarDays, Info } from 'lucide-react';
import { bereitschaft, type Ampel, type PersonAuslastung } from '../api';

/**
 * Wie sich die Bereitschaft verteilt — und wo sie sich häuft.
 *
 * ══ WOZU ═══════════════════════════════════════════════════════════════════
 *
 * Bereitschaft verteilt sich nicht von selbst gleichmäßig. Wer schnell
 * antwortet, steht in mehr Plänen; wer in mehr Plänen steht, wird öfter
 * geweckt. Das fällt lange nicht auf, weil niemand mitzählt.
 *
 * ══ BELASTUNG, NICHT LEISTUNG ══════════════════════════════════════════════
 *
 * Hier steht, **wie oft** jemand herangezogen wurde — nicht, wie schnell er
 * reagiert hat oder ob es gut ausging. Das ist keine Feinheit: eine Ansicht
 * mit Reaktionszeiten wäre ein anderes Werkzeug mit einem anderen Zweck, und
 * dieses hier soll die Last verteilen helfen, nicht Menschen vergleichen.
 *
 * ══ KEINE SPERREN ══════════════════════════════════════════════════════════
 *
 * Rot heisst nicht „geht nicht". Es heisst: sieh hin, bevor du ihn noch einmal
 * einträgst. Wer die Lage kennt, entscheidet besser als eine Regel — und ein
 * PC, der Dienstplanung verbietet, ersetzt kein Gespräch, er verhindert nur
 * die Eintragung.
 */

const AMPEL_STIL: Record<Ampel, { punkt: string; text: string; titel: string }> = {
  gruen: {
    punkt: 'bg-emerald-500',
    text: 'text-emerald-700',
    titel: 'Im üblichen Rahmen',
  },
  gelb: {
    punkt: 'bg-amber-500',
    text: 'text-amber-700',
    titel: 'Deutlich über dem Mittel — beim nächsten Plan mitdenken',
  },
  rot: {
    punkt: 'bg-rose-500',
    text: 'text-rose-700',
    titel: 'Trägt erheblich mehr als die anderen',
  },
  unbestimmt: {
    punkt: 'bg-slate-300',
    text: 'text-slate-500',
    titel: 'Zu wenige Ereignisse für eine belastbare Aussage',
  },
};

export function AmpelPunkt({ ampel, klein }: { ampel: Ampel; klein?: boolean }) {
  const s = AMPEL_STIL[ampel];
  return (
    <span
      title={s.titel}
      className={`inline-block shrink-0 rounded-full ${s.punkt} ${
        klein ? 'h-1.5 w-1.5' : 'h-2 w-2'
      }`}
    />
  );
}

function datum(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

function zeitpunkt(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function BereitschaftsAuslastung() {
  const [tage, setTage] = useState(90);
  const [offen, setOffen] = useState(false);
  const [protokollOffen, setProtokollOffen] = useState(false);

  const auslastung = useQuery({
    queryKey: ['bereitschaft', 'auslastung', tage],
    queryFn: () => bereitschaft.auslastung(tage),
  });

  const protokoll = useQuery({
    queryKey: ['bereitschaft', 'dienstprotokoll', tage],
    queryFn: () => bereitschaft.dienstprotokoll(tage),
    enabled: protokollOffen,
  });

  const b = auslastung.data;
  const beteiligt = (b?.personen ?? []).filter((p) => p.last > 0 || p.inPlaenen > 0);
  const auffaellig = beteiligt.filter((p) => p.ampel === 'gelb' || p.ampel === 'rot');

  return (
    <section>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <Scale size={14} /> Wie sich die Last verteilt
          {auffaellig.length > 0 && (
            <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
              {auffaellig.length} auffällig
            </span>
          )}
        </h2>
        <select
          value={tage}
          onChange={(e) => setTage(Number(e.target.value))}
          className="rounded border border-slate-300 px-2 py-1 text-xs"
        >
          <option value={30}>letzte 30 Tage</option>
          <option value={90}>letzte 90 Tage</option>
          <option value={180}>letzte 6 Monate</option>
          <option value={365}>letztes Jahr</option>
        </select>
      </div>

      <div className="rounded-md border border-slate-200 bg-white">
        {auslastung.isLoading && (
          <p className="px-3 py-4 text-sm text-slate-500">Wird gerechnet …</p>
        )}

        {b && beteiligt.length === 0 && (
          <p className="px-3 py-4 text-sm text-slate-500">
            Niemand steht in einem aktiven Plan, und es wurde niemand angesprochen.
          </p>
        )}

        {b && beteiligt.length > 0 && (
          <>
            {b.duenn && b.hinweis && (
              <p className="flex items-start gap-1.5 border-b border-slate-100 px-3 py-2 text-[11px] text-slate-500">
                <Info size={12} className="mt-px shrink-0" /> {b.hinweis}
              </p>
            )}

            <table className="w-full text-left text-xs">
              <thead className="border-b border-slate-100 text-[11px] text-slate-500">
                <tr>
                  <th className="px-3 py-1.5 font-medium">Person</th>
                  <th className="px-3 py-1.5 text-right font-medium">angesprochen</th>
                  <th className="px-3 py-1.5 text-right font-medium" title="Ausserhalb 8–18 Uhr">
                    abends/nachts
                  </th>
                  <th
                    className="px-3 py-1.5 text-right font-medium"
                    title="Wochenenden und Feiertage"
                  >
                    Ruhetage
                  </th>
                  <th className="px-3 py-1.5 text-right font-medium">übernommen</th>
                  <th className="px-3 py-1.5 text-right font-medium" title="In wievielen aktiven Plänen">
                    Pläne
                  </th>
                  <th className="px-3 py-1.5 text-right font-medium">zuletzt</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {(offen ? beteiligt : beteiligt.slice(0, 6)).map((p: PersonAuslastung) => (
                  <tr key={p.userId ?? p.email ?? p.name}>
                    <td className="px-3 py-1.5">
                      <span className="flex items-center gap-1.5">
                        <AmpelPunkt ampel={p.ampel} />
                        <span className="font-medium text-slate-800">{p.name}</span>
                        {p.ampel !== 'unbestimmt' && p.anteil > 0 && (
                          <span className={`text-[10px] ${AMPEL_STIL[p.ampel].text}`}>
                            {p.anteil.toFixed(1)}× Mittel
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">
                      {p.ansprachen}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">
                      {p.ausserhalb > 0 ? (
                        <span className="inline-flex items-center gap-1">
                          <Moon size={10} className="text-slate-400" /> {p.ausserhalb}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">
                      {p.ruhetage > 0 ? (
                        <span className="inline-flex items-center gap-1">
                          <CalendarDays size={10} className="text-slate-400" /> {p.ruhetage}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">
                      {p.uebernahmen || '—'}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">
                      {p.inPlaenen || '—'}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">
                      {datum(p.letzteAnsprache)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {beteiligt.length > 6 && (
              <button
                onClick={() => setOffen((v) => !v)}
                className="w-full border-t border-slate-100 px-3 py-1.5 text-left text-[11px] text-slate-500 hover:bg-slate-50"
              >
                {offen ? (
                  <>
                    <ChevronDown size={11} className="mr-1 inline" /> weniger
                  </>
                ) : (
                  <>
                    <ChevronRight size={11} className="mr-1 inline" /> alle{' '}
                    {beteiligt.length} anzeigen
                  </>
                )}
              </button>
            )}

            <p className="border-t border-slate-100 px-3 py-2 text-[10px] leading-relaxed text-slate-500">
              Gezählt wird, wie oft jemand herangezogen wurde — abends, an Wochenenden und an
              Feiertagen zählt schwerer, eine Übernahme mehr als eine Benachrichtigung. Nicht
              erfasst wird, wie schnell jemand reagiert hat: das hier soll Last verteilen helfen,
              nicht Menschen vergleichen. Gelb und Rot sperren nichts.
            </p>
          </>
        )}
      </div>

      {/* ── Dienstprotokoll ─────────────────────────────────────────────── */}
      <button
        onClick={() => setProtokollOffen((v) => !v)}
        className="mt-2 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-700"
      >
        {protokollOffen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
        Protokoll: wer wann dran war
      </button>

      {protokollOffen && (
        <div className="mt-2 max-h-96 overflow-y-auto rounded-md border border-slate-200 bg-white">
          {protokoll.isLoading && (
            <p className="px-3 py-3 text-xs text-slate-500">Wird geladen …</p>
          )}
          {protokoll.data && protokoll.data.length === 0 && (
            <p className="px-3 py-3 text-xs text-slate-500">
              In diesem Zeitraum wurde niemand herangezogen.
            </p>
          )}
          {protokoll.data && protokoll.data.length > 0 && (
            <table className="w-full text-left text-[11px]">
              <tbody className="divide-y divide-slate-50">
                {protokoll.data.map((e, i) => (
                  <tr key={`${e.zeitpunkt}-${e.person}-${i}`}>
                    <td className="whitespace-nowrap px-3 py-1 tabular-nums text-slate-500">
                      {zeitpunkt(e.zeitpunkt)}
                    </td>
                    <td className="px-3 py-1">
                      {e.lage === 'ruhetag' && (
                        <span title="Wochenende oder Feiertag">
                          <CalendarDays size={10} className="text-amber-600" />
                        </span>
                      )}
                      {e.lage === 'ausserhalb' && (
                        <span title="Ausserhalb der Geschäftszeiten">
                          <Moon size={10} className="text-slate-400" />
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1 font-medium text-slate-800">{e.person}</td>
                    <td className="px-3 py-1">
                      <span
                        className={`rounded px-1.5 py-0.5 text-[10px] ${
                          e.art === 'uebernahme'
                            ? 'bg-teal-100 text-teal-800'
                            : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {e.art === 'uebernahme' ? 'übernommen' : `Stufe ${e.stufe ?? '?'}`}
                      </span>
                    </td>
                    <td className="truncate px-3 py-1 text-slate-600" title={e.anlass ?? ''}>
                      {e.anlass ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1 text-slate-400">{e.plan ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
}
